import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import Notification from '../../../components/Notification/Notification'
import Loading from '../../../components/Loading/Loading'

import KampdagMatchModal from './KampdagMatchModal'

import { KAMPDAG_MATCHES } from '../../../graphql/queries'
import { DELETE_KAMPDAG_MATCH, UPSERT_KAMPDAG_MATCH } from '../../../graphql/mutations'

import './KampdagAdmin.css'
import { EMPTY_FORM } from './kampdagAdmin.constants'
import useInitialMatchEdit from '../hooks/useInitialMatchEdit'
import useNotification from '../../../hooks/useNotification'

function toLocalDatetimeValue(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalDatetimeValue(value) {
  if (!value) return null
  const d = new Date(value)
  return d.toISOString()
}

const toIntOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : null
}

export default function KampdagAdmin({
  initialEditMatchId = null,
  onInitialEditHandled,
}) {
  const { showNotification, notificationProps } = useNotification()
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const { data, loading, refetch } = useQuery(KAMPDAG_MATCHES, {
    fetchPolicy: 'cache-and-network',
    onError: (e) => showNotification(e.message || 'Kunne ikke hente kampdage.', 'error'),
  })
  const matches = useMemo(() => (Array.isArray(data?.kampdagMatches) ? data.kampdagMatches : []), [data])

  const [upsertMatch, { loading: saving }] = useMutation(UPSERT_KAMPDAG_MATCH, {
    onCompleted: () => {
      showNotification('Kampdag gemt.', 'success')
      setModalOpen(false)
      setForm(EMPTY_FORM)
      refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke gemme kampdag.', 'error'),
  })

  const [deleteMatch, { loading: deleting }] = useMutation(DELETE_KAMPDAG_MATCH, {
    onCompleted: () => {
      showNotification('Kampdag slettet.', 'success')
      refetch()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke slette kampdag.', 'error'),
  })

  const openNew = () => {
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = useCallback((m) => {
    setForm({
      id: m.id,
      title: m.title || '',
      kickoffLocal: toLocalDatetimeValue(m.kickoff),
      homeTeam: m.homeTeam || '',
      awayTeam: m.awayTeam || '',
      venue: m.venue || '',
      city: m.city || '',
      enableResult: !!m.enableResult,
      enableYellowCards: !!m.enableYellowCards,
      enableRedCards: !!m.enableRedCards,
      enableCorners: !!m.enableCorners,
      homeScore: m.homeScore ?? '',
      awayScore: m.awayScore ?? '',
      yellowCards: m.yellowCards ?? '',
      redCards: m.redCards ?? '',
      corners: m.corners ?? '',
    })
    setModalOpen(true)
  }, [])

  useInitialMatchEdit({
    initialEditMatchId,
    matches,
    loading,
    onOpenEdit: openEdit,
    onInitialEditHandled,
  })

  const submit = async () => {
    const kickoff = fromLocalDatetimeValue(form.kickoffLocal)
    if (!kickoff) {
      showNotification('Tidspunkt er påkrævet.', 'error')
      return
    }
    if (!form.homeTeam.trim() || !form.awayTeam.trim()) {
      showNotification('Hjemmehold og udehold er påkrævet.', 'error')
      return
    }
    if (!form.enableResult && !form.enableYellowCards && !form.enableRedCards && !form.enableCorners) {
      showNotification('Vælg mindst én kategori der skal spilles på.', 'error')
      return
    }

    await upsertMatch({
      variables: {
        input: {
          ...(form.id ? { id: form.id } : {}),
          title: form.title.trim() || null,
          kickoff,
          homeTeam: form.homeTeam.trim(),
          awayTeam: form.awayTeam.trim(),
          venue: form.venue.trim() || null,
          city: form.city.trim() || null,
          enableResult: !!form.enableResult,
          enableYellowCards: !!form.enableYellowCards,
          enableRedCards: !!form.enableRedCards,
          enableCorners: !!form.enableCorners,
          homeScore: form.enableResult ? toIntOrNull(form.homeScore) : null,
          awayScore: form.enableResult ? toIntOrNull(form.awayScore) : null,
          yellowCards: form.enableYellowCards ? toIntOrNull(form.yellowCards) : null,
          redCards: form.enableRedCards ? toIntOrNull(form.redCards) : null,
          corners: form.enableCorners ? toIntOrNull(form.corners) : null,
        },
      },
    })
  }

  const renderCategoryChips = (m) => {
    const chips = []
    if (m.enableResult) chips.push({ label: 'Resultat', value: `${m.homeScore ?? '-'}-${m.awayScore ?? '-'}` })
    if (m.enableYellowCards) chips.push({ label: 'Gule', value: m.yellowCards ?? '-' })
    if (m.enableRedCards) chips.push({ label: 'Røde', value: m.redCards ?? '-' })
    if (m.enableCorners) chips.push({ label: 'Hjørner', value: m.corners ?? '-' })
    return (
      <div className="kampdag-admin-chips">
        {chips.map((c) => (
          <span key={c.label} className="kampdag-admin-chip">
            <strong>{c.label}:</strong> {c.value}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div className="kampdag-admin">
      <Notification {...notificationProps} />

      <div className="admin-page-toolbar">
        <Button title="Tilføj kampdag" onClick={openNew} color="confirmation" />
      </div>

      {loading ? (
        <div style={{ paddingTop: 20 }}>
          <Loading />
        </div>
      ) : (
        <div className="kampdag-admin-list">
          {matches.length === 0 ? (
            <Card hover={false}>
              <div>Ingen kampdage endnu. Tryk “Tilføj kampdag”.</div>
            </Card>
          ) : (
            matches.map((m) => (
              <Card key={m.id} className="kampdag-admin-item" hover={false}>
                <div className="kampdag-admin-item__main">
                  <div className="kampdag-admin-item__title">
                    <strong>{m.title || `${m.homeTeam} vs ${m.awayTeam}`}</strong>
                  </div>
                  <div className="kampdag-admin-item__meta">
                    {m.homeTeam} vs {m.awayTeam}
                  </div>
                  <div className="kampdag-admin-item__meta">
                    {new Date(m.kickoff).toLocaleString('da-DK')}
                    {m.venue ? ` · ${m.venue}` : ''}
                    {m.city ? `, ${m.city}` : ''}
                  </div>
                  {renderCategoryChips(m)}
                </div>

                <div className="kampdag-admin-item__actions">
                  <Button title="Redigér" onClick={() => openEdit(m)} />
                  <Button
                    title="Slet"
                    variant="outlined"
                    color="danger"
                    disabled={deleting}
                    onClick={() => deleteMatch({ variables: { id: m.id } })}
                  />
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      <KampdagMatchModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        form={form}
        setForm={setForm}
        onSubmit={submit}
        saving={saving}
      />
    </div>
  )
}
